import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import classNames from 'classnames';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import { FormattedMessage } from '../../util/reactIntl';
import CartModalHeader from './CartModalHeader/CartModalHeader';
import CartModalItem from './CartModalItem/CartModalItem';
import CartModalFooter, { CartModalCta } from './CartModalFooter/CartModalFooter';
import CartDatesEditor from '../CartDrawer/CartDatesEditor';
import { buildFetchLineItemsPayload, checkExpiredRentalDates } from '../../util/cartHelpers';
import CartExpiredDatesNotice from '../CartShared/CartExpiredDatesNotice';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import {
  getHighestRateDeliveryPickupFees,
  getCartDeliveryPickupConfig,
  getCartAddonsConfig,
  buildCartModalInitialValues,
  buildItemsForAPI,
  getChangedCartItems,
} from './cartModalHelpers';
import { validateDeliveryPickupSelection } from '../OrderPanel/OrderPanelHelpers';
import { DELIVERY_FEE, PICKUP_FEE, USAGE_MULTIPLE } from '../../util/constants';
import css from './CartModal.module.css';

const ANIMATION_DURATION = 350; // ms
const FETCH_DEBOUNCE_DELAY = 500; // ms

const CartModal = props => {
  const {
    isOpen,
    onClose,
    // Exactly one (store, date range) group - StoreProfilePage scopes it before it
    // ever reaches here, same contract as the listing page's CartDrawer.
    items = [],
    dateRange,
    timeZone,
    intl,
    // Whether the whole cart holds more orders than the one on screen - shows the
    // header's "All orders" link, which swaps this panel for the all-orders panel.
    hasOtherCartGroups = false,
    onViewAllOrders,
    // Date editing (the listing drawer's own CartDatesEditor, reused as-is): one
    // atomic write of the new dates for every item in this group, and a callback so
    // the parent re-scopes to the new dates once the write has landed.
    onSaveCartItemSelectionsBatch,
    onDatesApplied,
    onUpdateCart,
    onRemoveFromCart,
    onRentNow,
    onFetchTransactionLineItems,
    currentUserCurrency,
    exchangeRates,
    marketplaceName,
    marketplaceCurrency,
    listing,
    lineItems: reduxLineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    isOwner = false,
    showAddressInStore = false,
  } = props;

  const [show, setShow] = useState(isOpen);
  const [closing, setClosing] = useState(false);
  const [deliveryPickupError, setDeliveryPickupError] = useState(null);
  // While the inline date picker is open, Rent Now is disabled - the shopper must
  // Apply or discard first (RAS-2511/2512, same as CartDrawer).
  const [isEditingDates, setIsEditingDates] = useState(false);

  // Expired cart (start day already passed): say so at the top, point at the date
  // editor, and refuse Rent Now until new dates are applied - same as the listing
  // page's CartDrawer (client bug report 2026-09-18: an expired store cart could be
  // submitted and only failed at checkout).
  const hasExpiredDates = useMemo(() => checkExpiredRentalDates(items).hasExpiredDates, [items]);
  const [dateEditorOpenRequest, setDateEditorOpenRequest] = useState(0);
  const datesSectionRef = useRef(null);
  const handleUpdateExpiredDates = useCallback(() => {
    setDateEditorOpenRequest(n => n + 1);
    datesSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, []);
  const contentRef = useRef(null);
  // Every open starts scrolled to the top so the rows just added are the first thing
  // on screen (the panel unmounts on close today, but don't depend on that).
  useEffect(() => {
    if (isOpen && contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [isOpen]);
  const [showBrowseToast, setShowBrowseToast] = useState(false);
  const formRef = useRef(null);

  // Track if we've fetched initial pricing
  const hasFetchedInitialRef = useRef(false);
  const isFetchingRef = useRef(false);
  const deliveryPickupRef = useRef(null);

  // Track serialized form values to detect actual changes
  const formValuesSerializedRef = useRef('');

  // Get highest rate delivery/pickup fees from all items
  const highestRateDeliveryPickup = useMemo(
    () =>
      getHighestRateDeliveryPickupFees(
        items,
        exchangeRates,
        currentUserCurrency
      ),
    [items, exchangeRates, currentUserCurrency]
  );

  // Get delivery/pickup configuration (combine from all items)
  const deliveryPickupConfig = useMemo(
    () =>
      getCartDeliveryPickupConfig(
        items,
        highestRateDeliveryPickup,
        exchangeRates,
        currentUserCurrency
      ),
    [items, highestRateDeliveryPickup, exchangeRates, currentUserCurrency]
  );

  // Same "oncePerCart, highest wins" rule as delivery/pickup above, generalized to
  // every other addon fee (cleaning fee, setup fee, etc.) - mirrors CartDrawer's own
  // wiring of this helper. Feeds the shared, cart-level add-ons section in
  // CartModalFooter so a fee configured to charge once for the whole cart renders (and
  // can be selected) once, instead of once per item.
  const addonsConfig = useMemo(
    () => getCartAddonsConfig(items, exchangeRates, currentUserCurrency),
    [items, exchangeRates, currentUserCurrency]
  );

  // Build initial values (apply BookingDatesForm rules)
  const initialValues = useMemo(
    () =>
      buildCartModalInitialValues(
        items,
        deliveryPickupConfig,
        exchangeRates,
        currentUserCurrency
      ),
    [items, deliveryPickupConfig, exchangeRates, currentUserCurrency]
  );

  // Fetch line items function - now uses form values
  const fetchLineItems = useCallback(
    async formValues => {
      if (
        !isOpen ||
        !items.length ||
        isFetchingRef.current ||
        fetchLineItemsInProgress
      ) {
        return;
      }

      isFetchingRef.current = true;

      try {
        const itemsWithFormValues = buildItemsForAPI(formValues, items);
        const fetchLineItemsData = buildFetchLineItemsPayload(
          itemsWithFormValues
        );

        if (!fetchLineItemsData.length) {
          isFetchingRef.current = false;
          return;
        }

        await onFetchTransactionLineItems({
          fetchLineItemsData: fetchLineItemsData.map(data => ({
            ...data,
            isOwnListing: false,
          })),
        });
      } catch (error) {
        // Error is handled by Redux and displayed via fetchLineItemsError prop
        console.error('Error fetching line items:', error);
      } finally {
        isFetchingRef.current = false;
      }
    },
    [isOpen, items, onFetchTransactionLineItems, fetchLineItemsInProgress]
  );

  // Create debounced fetch function with proper cleanup
  const debouncedFetchLineItems = useMemo(
    () =>
      debounce(formValues => {
        fetchLineItems(formValues);
      }, FETCH_DEBOUNCE_DELAY),
    [fetchLineItems]
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedFetchLineItems.cancel();
    };
  }, [debouncedFetchLineItems]);

  // Fetch initial pricing when modal opens (single form)
  useEffect(() => {
    if (isOpen && items.length && !hasFetchedInitialRef.current) {
      hasFetchedInitialRef.current = true;
      const timer = setTimeout(() => {
        const values = formRef.current?.getState?.()?.values;
        if (values) {
          fetchLineItems(values);
        }
      }, 100);

      return () => clearTimeout(timer);
    }

    // Reset flags when modal closes
    if (!isOpen) {
      hasFetchedInitialRef.current = false;
      isFetchingRef.current = false;
      debouncedFetchLineItems.cancel();
      formValuesSerializedRef.current = '';
      setDeliveryPickupError(null);
    }
  }, [isOpen, items.length, fetchLineItems, debouncedFetchLineItems]);

  // Re-price whenever the group's items or their booking dates change while the panel
  // is open - a date Apply landing in Redux, or an item removed. Mirrors CartDrawer's
  // RAS-2513 effect: reactive to the `items` prop rather than fired from the Apply
  // handler, since the items only carry the new dates once the batched write has
  // propagated back down; fetching from inside the handler would price the OLD
  // dates. The FormSpy below can't catch this - dates and membership aren't form
  // values - so quantity edits still re-price through it, but a date change would
  // otherwise leave the total priced for the wrong days. Skips the first render after
  // open (the initial fetch above already covers it).
  const itemsSignatureRef = useRef('');
  useEffect(() => {
    if (!isOpen) {
      itemsSignatureRef.current = '';
      return;
    }
    const signature = items
      .map(item => `${item.id}:${item.bookingDates?.startDate}:${item.bookingDates?.endDate}`)
      .join('|');
    if (itemsSignatureRef.current && itemsSignatureRef.current !== signature) {
      const values = formRef.current?.getState?.()?.values;
      if (values) {
        fetchLineItems(values);
      }
    }
    itemsSignatureRef.current = signature;
  }, [isOpen, items, fetchLineItems]);

  // Save all form values to Redux - now uses form values
  const saveAllFormValues = useCallback(
    (itemsWithFormValues = []) => {
      // Update each item in Redux
      itemsWithFormValues.forEach(item => {
        const updateData = {
          id: item.id,
          authorId: item.authorId,
          quantity: item.quantity,
          usageMultipleValues: item.usageMultipleValues || {},
          selectedFeeTypes: item.selectedFeeTypes || [], // FIX: Use selectedFeeTypes instead of otherOptionalFees
          pickupAtOwnerLocation: item.pickupAtOwnerLocation,
          returnAtOwnerLocation: item.returnAtOwnerLocation,
        };
        onUpdateCart(updateData);
      });
    },
    [onUpdateCart]
  );

  // RAS-2582: guards against persisting the same edits twice - once explicitly via
  // Rent Now, and again when the modal closing as a result is picked up by the
  // isOpen-transition effect below.
  const hasPersistedRef = useRef(false);
  // Always-fresh copy of `items` for the unmount safety-net effect, whose cleanup
  // closure would otherwise only ever see the items from its first render.
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // RAS-2582: persists any pending edits (quantity, fees, pickup/return location)
  // made in the Store Cart, diffed against the items it was opened with so a plain
  // open-then-close with no edits never fires a write. Called from the isOpen
  // transition effect and the unmount safety net below - not from the individual
  // close affordances - so every way of leaving the cart is covered uniformly.
  const persistPendingChanges = useCallback(() => {
    if (hasPersistedRef.current) return;
    const values = formRef.current?.getState?.()?.values;
    if (!values) return;
    const changedItems = getChangedCartItems(values, itemsRef.current);
    if (changedItems.length) {
      hasPersistedRef.current = true;
      saveAllFormValues(changedItems);
    }
  }, [saveAllFormValues]);

  // Handle modal close (X, overlay click, "Shop" pill, header's "Continue shopping")
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Removes a shared (oncePerCart) checkbox add-on. Unlike CartDrawer, nothing here
  // needs to persist immediately - the Store Cart form only writes to Redux on close
  // or Rent Now (see persistPendingChanges/handleRentNow above), so a plain form field
  // clear is enough; buildItemsForAPI already reads global.optionalFees for every item
  // that has this feeType configured (see cartModalHelpers.js), so clearing it here
  // deselects it everywhere at once.
  const handleRemoveGlobalOptionalFee = useCallback(feeType => {
    const form = formRef.current;
    if (!form) return;
    const current = form.getState()?.values?.global?.optionalFees || [];
    form.change('global.optionalFees', current.filter(ft => ft !== feeType));
  }, []);

  // Same as above, for a shared usage-multiple add-on's numeric value.
  const handleRemoveGlobalUsageFee = useCallback(feeType => {
    const form = formRef.current;
    if (!form) return;
    form.change(`global.addonUsage.${feeType}`, undefined);
  }, []);

  // Handle rent now - validate and save form values then proceed
  const handleRentNow = useCallback(
    async formValues => {
      if (!formValues) return;

      // Validate delivery/pickup (same as BookingDatesForm)
      const validation = validateDeliveryPickupSelection(
        deliveryPickupConfig?.deliveryPickup,
        formValues,
        DELIVERY_FEE,
        PICKUP_FEE,
        USAGE_MULTIPLE,
        true // isCartModal = true
      );

      if (!validation.isValid) {
        setDeliveryPickupError(validation.error);
        if (deliveryPickupRef?.current) {
          setTimeout(() => {
            deliveryPickupRef.current.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
        return;
      }

      // Clear errors
      setDeliveryPickupError(null);

      // Build updated items from form values
      const itemsWithFormValues = buildItemsForAPI(formValues, items);

      // Save to Redux (async, but we'll use itemsWithFormValues directly). Marked
      // persisted up front so the isOpen-transition effect (fired by the navigation
      // this triggers) doesn't redundantly re-save the same items a second time.
      hasPersistedRef.current = true;
      saveAllFormValues(itemsWithFormValues);

      // Pass updated items directly to onRentNow
      onRentNow(itemsWithFormValues);
    },
    [deliveryPickupConfig, saveAllFormValues, onRentNow, items]
  );

  // Single FormSpy for entire form
  const handleFormChange = useCallback(
    formState => {
      if (!formState.values || !isOpen || !items.length) {
        return;
      }

      // Serialize current form values
      const currentSerialized = JSON.stringify(formState.values);

      // Initialize serialized values on first run (after initial fetch)
      if (!hasFetchedInitialRef.current) {
        return;
      }

      // Only trigger if values actually changed and we've done initial fetch
      if (
        hasFetchedInitialRef.current &&
        currentSerialized !== formValuesSerializedRef.current
      ) {
        formValuesSerializedRef.current = currentSerialized;
        debouncedFetchLineItems(formState.values);
      }
    },
    [isOpen, items.length, debouncedFetchLineItems]
  );

  // Modal show/hide animation
  useEffect(() => {
    if (isOpen) {
      setShow(true);
      setClosing(false);
    } else if (show) {
      setClosing(true);
      const timer = setTimeout(() => {
        setShow(false);
        setClosing(false);
      }, ANIMATION_DURATION);
      return () => clearTimeout(timer);
    }
  }, [isOpen, show]);

  // RAS-2582: persist pending edits whenever the modal transitions from open to
  // closed, regardless of what triggered it - an explicit close affordance, or a
  // parent-driven close such as the browser back button removing the ?storeCart=
  // URL param (StoreProfilePage flips `isOpen` directly in that case, bypassing
  // every click handler in this component).
  const prevIsOpenRef = useRef(isOpen);
  useEffect(() => {
    if (isOpen) {
      hasPersistedRef.current = false;
    } else if (prevIsOpenRef.current) {
      persistPendingChanges();
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, persistPendingChanges]);

  // RAS-2582: safety net for the store page unmounting entirely while the cart is
  // still open (e.g. navigating away without an explicit close). Reads the persist
  // function from a ref so this cleanup - which must have an empty dependency array
  // to only run on true unmount - always sees the latest closure, not the one from
  // first mount.
  const persistPendingChangesRef = useRef(persistPendingChanges);
  useEffect(() => {
    persistPendingChangesRef.current = persistPendingChanges;
  }, [persistPendingChanges]);
  useEffect(() => {
    return () => {
      persistPendingChangesRef.current();
    };
  }, []);

  // One-time session toast on first cart open
  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return;
    const key = 'rasCartBrowseToastShown';
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, '1');
      setShowBrowseToast(true);
      const timer = setTimeout(() => setShowBrowseToast(false), 2500);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!show) return null;

  return (
    <FinalForm
      initialValues={initialValues}
      initialValuesEqual={(a, b) => isEqual(a, b)}
      keepDirtyOnReinitialize={true}
      onSubmit={() => {}}
      render={({ form, values }) => {
        // Store form reference for access in callbacks
        formRef.current = form;

        return (
          <>
          <div
            className={css.overlay}
            onClick={e => {
              if (e.target === e.currentTarget) handleClose();
            }}
          >
            <aside
              className={classNames(css.popup, { [css.closing]: closing })}
            >
              {/* Header */}
              <CartModalHeader
                itemsCount={items.length}
                // The header's date text is only a stand-in for an EMPTY panel (the
                // page's date filter, so the shopper knows which dates "empty" is
                // about). With items, the editable Start/End fields at the top of the
                // content below are the dates - echoing them here too was the old
                // layout's leftover.
                bookingDates={items.length === 0 ? dateRange : null}
                timeZone={items[0]?.timeZone || timeZone}
                intl={intl}
                onClose={handleClose}
                hasOtherCartGroups={hasOtherCartGroups}
                onViewAllOrders={onViewAllOrders}
              />

              {/* Content - the ONE scroll region: item rows first, then the Order
                  Summary (dates, delivery/pickup, add-ons, price breakdown). Header
                  above and the Rent Now bar below are pinned. Same structure as the
                  listing page's CartDrawer - see its .body comment for why nested or
                  sticky-peeking regions don't survive contact with a phone. */}
              <div className={css.content} ref={contentRef}>
                {items.length === 0 ? (
                  <div className={css.emptyState}>
                    <FormattedMessage id="CartModal.emptyCart" />
                  </div>
                ) : (
                  <>
                    {/* Rental dates first, under the pinned header - same row, same
                        place as the listing page's CartDrawer (client feedback
                        2026-09-17: it used to sit in the Order Summary below every
                        item). Inside the scroll region so the calendar it expands
                        into scrolls with the order rather than covering it. */}
                    {onSaveCartItemSelectionsBatch ? (
                      <div className={css.datesSection} ref={datesSectionRef}>
                        {hasExpiredDates ? (
                          <CartExpiredDatesNotice onUpdateDates={handleUpdateExpiredDates} />
                        ) : null}
                        <CartDatesEditor
                          isOpen={isOpen}
                          items={items}
                          timeZone={items[0]?.timeZone || timeZone || 'Etc/UTC'}
                          intl={intl}
                          onSaveCartItemSelectionsBatch={onSaveCartItemSelectionsBatch}
                          onDatesApplied={onDatesApplied}
                          onEditingChange={setIsEditingDates}
                          openRequest={dateEditorOpenRequest}
                        />
                      </div>
                    ) : hasExpiredDates ? (
                      // A host that can't edit dates in place still must not let an
                      // expired cart be submitted; the notice's button is a no-op then
                      // but the message and the disabled CTA stand.
                      <div className={css.datesSection}>
                        <CartExpiredDatesNotice onUpdateDates={() => {}} />
                      </div>
                    ) : null}
                    {fetchLineItemsError && (
                      <div className={css.errorMessage}>
                        <FormattedMessage
                          id="CartModal.fetchError"
                          defaultMessage="Unable to load pricing. Please try again."
                        />
                      </div>
                    )}
                    {items.map(item => (
                      <CartModalItem
                        key={item.id}
                        item={item}
                        intl={intl}
                        form={form}
                        formValues={
                          (values.items && values.items[item.id]) || {}
                        }
                        onRemoveFromCart={onRemoveFromCart}
                        fetchLineItemsInProgress={fetchLineItemsInProgress}
                        currentUserCurrency={currentUserCurrency}
                        exchangeRates={exchangeRates}
                      />
                    ))}
                  </>
                )}

              {/* Order Summary - Dates, Delivery/Pickup, Add-ons, Pricing (CTA pinned below) */}
              {items.length > 0 && (
                <CartModalFooter
                  renderCta={false}
                  items={items}
                  intl={intl}
                  form={form}
                  formValues={values}
                  isEditingDates={isEditingDates}
                  deliveryPickup={deliveryPickupConfig?.deliveryPickup}
                  listing={deliveryPickupConfig?.listing || listing}
                  addons={addonsConfig?.globalAddons}
                  onRemoveGlobalOptionalFee={handleRemoveGlobalOptionalFee}
                  onRemoveGlobalUsageFee={handleRemoveGlobalUsageFee}
                  onRentNow={() => handleRentNow(values)}
                  fetchLineItemsInProgress={fetchLineItemsInProgress}
                  lineItems={reduxLineItems}
                  currentUserCurrency={currentUserCurrency}
                  exchangeRates={exchangeRates}
                  marketplaceName={marketplaceName}
                  marketplaceCurrency={marketplaceCurrency}
                  deliveryPickupError={deliveryPickupError}
                  deliveryPickupRef={deliveryPickupRef}
                  isOwner={isOwner}
                  showAddressInStore={showAddressInStore}
                />
              )}
              </div>

              {items.length > 0 && (
                <div className={css.ctaBar}>
                  <CartModalCta
                    onRentNow={() => handleRentNow(values)}
                    fetchLineItemsInProgress={fetchLineItemsInProgress}
                    lineItems={reduxLineItems}
                    isEditingDates={isEditingDates}
                    hasExpiredDates={hasExpiredDates}
                  />
                </div>
              )}

              {/* Single FormSpy */}
              <FormSpy
                subscription={{ values: true }}
                onChange={handleFormChange}
              />
            </aside>
          </div>
          <button
            type="button"
            className={css.shopHint}
            onClick={e => { e.stopPropagation(); handleClose(); }}
            aria-label={intl.formatMessage({ id: 'CartModal.closeCartContinueShopping' })}
          >
            <svg width="10" height="14" viewBox="0 0 10 14" fill="none">
              <path d="M8 1L2 7L8 13" stroke="#555" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
            <span className={css.shopText}>Shop</span>
          </button>
          {showBrowseToast && (
            <div className={css.browseToast} role="status" aria-live="polite">
              {intl.formatMessage({ id: 'CartModal.browseHint' })}
            </div>
          )}
          </>
        );
      }}
    />
  );
};

export default CartModal;
